import type { MentoringDiscount } from '@algoacademy/backend/modules/payment/discount-pricing'
import {
  PAYMENT__DISCOUNT_SHAPE,
  type PaymentDiscountShape
} from '@algoacademy/backend/modules/payment/enums'
import {
  calculateRenewalPriceZloty,
  type MentoringOfferProduct
} from '../../../_forms/MentoringOfferForm/_helpers/mentoring-terms'
import { MENTORING_SUBSCRIPTION_PERIOD_COUNT } from '../../../prices'

/** Stripe charges in the smallest currency unit, so the API speaks grosze. */
const GROSZE_PER_ZLOTY = 100

/** A whole 90% off is already a gift; 100% off is a free subscription, not a discount. */
export const MINIMUM_DISCOUNT_PERCENT = 1
export const MAXIMUM_DISCOUNT_PERCENT = 90

/** The złoty shape starts at 1 zł; its ceiling follows the product, see readMaximumDiscountZloty. */
export const MINIMUM_DISCOUNT_ZLOTY = 1

/** Duration is counted in billing periods, matching the cadence from the card above. */
export const MINIMUM_DISCOUNT_PERIOD_COUNT = 1
export const MAXIMUM_DISCOUNT_PERIOD_COUNT = 999

/** Opening figures staff sees the moment they pick a shape, both easy to overtype. */
const DEFAULT_DISCOUNT_PERCENT = 10
const DEFAULT_DISCOUNT_ZLOTY = 100

export type MentoringDiscountDraft = {
  /** null means full price - the controls are collapsed and nothing is sent */
  shape: PaymentDiscountShape | null
  /** percent 1-90, or złoty off one billing period (invoice); null while staff clears the box */
  value: number | null
  /** Billing periods (invoices) covered; null means it never ends */
  periodCount: number | null
  reason: string
}

export const EMPTY_MENTORING_DISCOUNT: MentoringDiscountDraft = {
  shape: null,
  value: DEFAULT_DISCOUNT_PERCENT,
  periodCount: null,
  reason: ''
}

/**
 * What the offer mutation takes. Built on the backend's own discount type, so a
 * change to what pricing needs cannot drift away from what this control sends.
 */
export type MentoringDiscountInput = MentoringDiscount & {
  /** Never required - an empty box means staff had nothing to add, not an empty note. */
  reason: string | undefined
}

/** The two shapes open on different numbers, so switching shape retypes the value. */
export function readDefaultDiscountValue(shape: PaymentDiscountShape): number {
  return shape === PAYMENT__DISCOUNT_SHAPE.PERCENT
    ? DEFAULT_DISCOUNT_PERCENT
    : DEFAULT_DISCOUNT_ZLOTY
}

/**
 * What one billing period (invoice) of the configured product costs after the
 * cadence discount from the card above - the same figure checkout uses.
 */
export function readInvoiceEffectivePriceZloty(mentoringProduct: MentoringOfferProduct): number {
  return calculateRenewalPriceZloty(mentoringProduct)
}

/** @deprecated Use readInvoiceEffectivePriceZloty. */
export function readFourWeekEffectivePriceZloty(mentoringProduct: MentoringOfferProduct): number {
  const invoicePrice = calculateRenewalPriceZloty(mentoringProduct)
  const periodsCount = MENTORING_SUBSCRIPTION_PERIOD_COUNT[mentoringProduct.subscription]

  return Math.round(invoicePrice / periodsCount)
}

/** One złoty short of the invoice price - a period always has to cost something. */
export function readMaximumDiscountZloty(mentoringProduct: MentoringOfferProduct): number {
  return readInvoiceEffectivePriceZloty(mentoringProduct) - 1
}

function clampWholeNumber(value: number, minimum: number, maximum: number): number {
  // A cleared number input reads as NaN, which would otherwise poison every
  // price on screen; the lowest legal figure is the honest thing to show.
  if (!Number.isFinite(value)) return minimum

  return Math.min(Math.max(Math.round(value), minimum), maximum)
}

/** Clamps what staff typed in the discount value box once they leave the field. */
export function readClampedDiscountFieldValue(
  raw: string,
  minimum: number,
  maximum: number
): number {
  return clampWholeNumber(Number(raw.trim()), minimum, maximum)
}

/**
 * Parses each keystroke for live pricing. An empty box stays empty so staff can
 * retype; anything illegal snaps to the nearest whole number in range.
 */
export function parseLiveDiscountFieldValue(
  raw: string,
  minimum: number,
  maximum: number
): number | null {
  const trimmed = raw.trim()

  if (trimmed === '') return null

  const parsed = Number(trimmed)

  if (!Number.isFinite(parsed)) return null

  return clampWholeNumber(parsed, minimum, maximum)
}

/**
 * Cuts a draft back to what the configured product can carry. The mentoring type
 * and the hours live above this control and change without asking it, so a 3 000 zł
 * discount picked on the priciest product must not survive a switch to a cheaper
 * one. The same draft comes back untouched - the identical object, not a copy - so
 * a caller that re-clamps on every render does not chase its own tail.
 */
export function clampMentoringDiscountToProduct(args: {
  draft: MentoringDiscountDraft
  mentoringProduct: MentoringOfferProduct
}): MentoringDiscountDraft {
  const { draft, mentoringProduct } = args

  // Full price carries no figures worth checking.
  if (draft.shape === null) return draft

  const value =
    draft.value === null
      ? null
      : draft.shape === PAYMENT__DISCOUNT_SHAPE.PERCENT
        ? clampWholeNumber(draft.value, MINIMUM_DISCOUNT_PERCENT, MAXIMUM_DISCOUNT_PERCENT)
        : clampWholeNumber(
            draft.value,
            MINIMUM_DISCOUNT_ZLOTY,
            readMaximumDiscountZloty(mentoringProduct)
          )

  const periodCount =
    draft.periodCount === null
      ? null
      : clampWholeNumber(draft.periodCount, MINIMUM_DISCOUNT_PERIOD_COUNT, Number.MAX_SAFE_INTEGER)

  const reason = draft.reason

  if (value === draft.value && periodCount === draft.periodCount && reason === draft.reason) {
    return draft
  }

  return { ...draft, value, periodCount, reason }
}

/**
 * The one place a draft becomes what the API stores. The percent goes as typed,
 * the złoty amount becomes grosze, and an untouched reason box becomes nothing at
 * all rather than an empty string.
 *
 * Full price comes back as null, not as a missing field: only a screen that shows
 * these controls can say "list price on purpose", and that is how the offer drops
 * a discount it was carrying. A missing field means the sender knows nothing about
 * discounts, which is never true of a form holding this component.
 */
export function toMentoringDiscountInput(
  draft: MentoringDiscountDraft
): MentoringDiscountInput | null {
  if (draft.shape === null || draft.value === null) return null

  const reason = draft.reason.trim()

  return {
    shape: draft.shape,
    // Stripe refuses fractions of a grosz, so the złoty figure is squared off here.
    value:
      draft.shape === PAYMENT__DISCOUNT_SHAPE.AMOUNT
        ? Math.round(draft.value * GROSZE_PER_ZLOTY)
        : draft.value,
    periodCount: draft.periodCount,
    reason: reason === '' ? undefined : reason
  }
}
